import { AMENAGEMENT_CLE_EN_MAIN_URL, CONTACT_EMAIL, MEUBLES_URL, NEWSLETTER_URL, PACK_AMENAGEMENT_URL, TENTE_DE_TOIT_IMAGE, TENTE_DE_TOIT_URL, VANS_FOR_RENT_URL, VANS_FOR_SALE_URL } from '@/config/links'
import { useLanguage } from '@/context/LanguageContext'
import LinkButton from '@/components/LinkButton'
import {BookOpen, ChevronDown, Hammer, Key, Mail, MapPinned, Send, Sofa, Tag, Wrench, Truck, Tent} from 'lucide-react'
import { useState } from 'react'

interface CategoryLink {
  icon: typeof Hammer
  title: string
  href: string
  external: boolean
  image?: string
}

interface Category {
  key: string
  icon: typeof Wrench
  title: string
  subtitle: string
  links: CategoryLink[]
}

function LinksList() {
  const { t } = useLanguage()
  const [openKey, setOpenKey] = useState<string | null>('amenagement')

  const categories: Category[] = [
    {
      key: 'amenagement',
      icon: Wrench,
      title: t.linkCategories.amenagementTitle,
      subtitle: t.linkCategories.amenagementSubtitle,
      links: [
        { icon: Hammer, title: t.quickLinks.amenagement, href: AMENAGEMENT_CLE_EN_MAIN_URL, external: true },
        { icon: Sofa, title: t.quickLinks.meubles, href: MEUBLES_URL, external: true },
        { icon: Send, title: t.quickLinks.devis, href: `mailto:${CONTACT_EMAIL}`, external: false },
      ],
    },
    {
      key: 'vans',
      icon: Truck,
      title: t.linkCategories.vansTitle,
      subtitle: t.linkCategories.vansSubtitle,
      links: [
        { icon: Key, title: t.quickLinks.location, href: VANS_FOR_RENT_URL, external: true },
        { icon: Tag, title: t.quickLinks.vente, href: VANS_FOR_SALE_URL, external: true },
      ],
    },
    {
      key: 'tenteDeToit',
      icon: Tent,
      title: t.linkCategories.tenteDeToitTitle,
      subtitle: t.linkCategories.tenteDeToitSubtitle,
      links: [
        { icon: Tent, title: t.quickLinks.tenteDeToit, href: TENTE_DE_TOIT_URL, external: true, image: TENTE_DE_TOIT_IMAGE },
      ],
    },
    {
      key: 'ressources',
      icon: BookOpen,
      title: t.linkCategories.resourcesTitle,
      subtitle: t.linkCategories.resourcesSubtitle,
      links: [
        { icon: MapPinned, title: t.quickLinks.guide, href: PACK_AMENAGEMENT_URL, external: true },
        { icon: BookOpen, title: t.quickLinks.ebook, href: '/ebook', external: false },
      ],
    },
    {
      key: 'newsletter',
      icon: Mail,
      title: t.linkCategories.newsletterTitle,
      subtitle: t.linkCategories.newsletterSubtitle,
      links: [
        { icon: Mail, title: t.quickLinks.newsletter, href: NEWSLETTER_URL, external: true },
      ],
    },
  ]

  const toggle = (key: string) => {
    setOpenKey(prev => (prev === key ? null : key))
  }

  return (
    <section className="bg-[#2b1c12] pb-4">
      <div className="max-w-md mx-auto px-4 flex flex-col gap-3">
        {categories.map((category, index) => {
          const isOpen = openKey === category.key
          const CategoryIcon = category.icon

          return (
            <div
              key={category.key}
              style={{ animationDelay: `${index * 60}ms` }}
              className="rounded-2xl bg-[#3d2b1f] border border-amber-800/30 overflow-hidden animate-fade-in-up"
            >
              <button
                type="button"
                onClick={() => toggle(category.key)}
                aria-expanded={isOpen}
                className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-[#4a3626] transition-colors"
              >
                <div className="w-11 h-11 rounded-xl bg-amber-500/15 flex items-center justify-center shrink-0">
                  <CategoryIcon className="w-5 h-5 text-amber-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-amber-50 font-semibold text-base leading-tight">{category.title}</p>
                  <p className="text-amber-100/60 text-sm mt-0.5 leading-snug truncate">{category.subtitle}</p>
                </div>
                <ChevronDown
                  className={`w-5 h-5 text-amber-400/70 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {isOpen ? (
                <div className="px-3 pb-3 pt-1 flex flex-col gap-2">
                  {category.links.map(link => (
                    <LinkButton
                      key={link.title}
                      icon={link.icon}
                      title={link.title}
                      href={link.href}
                      external={link.external}
                      image={link.image}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          )
        })}
      </div>
    </section>
  )
}

export default LinksList
